import React, { useState } from 'react';
import { passwordResetAPI } from '../services/api';
import './ForgotPassword.css';

interface ForgotPasswordProps {
  onBack: () => void;
  initialEmail?: string;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack, initialEmail = '' }) => {
  const [email, setEmail] = useState(initialEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Por favor introduce tu email');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await passwordResetAPI.requestReset(email.trim());
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al enviar el email de recuperación');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header success">
            <span className="forgot-password-icon">✅</span>
            <h2>Email Enviado</h2>
          </div>

          <div className="forgot-password-content">
            <div className="success-message">
              <p>
                Si el email <strong>{email}</strong> está registrado en nuestro sistema, 
                recibirás un enlace para restablecer tu contraseña en los próximos minutos.
              </p>
            </div>

            <div className="forgot-password-instructions">
              <h3>Instrucciones:</h3>
              <ol>
                <li>Revisa tu bandeja de entrada (y carpeta de spam)</li>
                <li>Haz clic en el enlace del email</li>
                <li>Introduce tu nueva contraseña</li>
                <li>¡Listo! Ya puedes iniciar sesión</li>
              </ol>
            </div>

            <div className="forgot-password-note">
              <p>
                <strong>Nota:</strong> El enlace expira en 30 minutos por seguridad.
                Si no recibes el email, puedes solicitar uno nuevo.
              </p>
            </div>

            <div className="forgot-password-actions">
              <button
                type="button"
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
                className="btn-secondary"
              >
                Enviar Otro Email
              </button>
              
              <button
                type="button"
                onClick={onBack}
                className="btn-primary"
              >
                Volver al Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <div className="forgot-password-header">
          <span className="forgot-password-icon">🔑</span>
          <h2>¿Olvidaste tu contraseña?</h2>
          <p>Te enviaremos un enlace para que puedas restablecerla</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="forgot-password-form">
          <div className="form-group">
            <label htmlFor="email">Email de tu cuenta</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
              autoComplete="email"
              autoFocus
            />
            <small className="form-help">
              Introduce el email que usaste para registrarte
            </small>
          </div>

          <div className="forgot-password-actions">
            <button
              type="button"
              onClick={onBack}
              className="btn-secondary"
              disabled={isLoading}
            >
              ← Volver al Login
            </button>
            
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Enviando...
                </>
              ) : (
                <>
                  📧 Enviar Enlace
                </>
              )}
            </button>
          </div>
        </form>

        <div className="forgot-password-help">
          <h3>¿Necesitas ayuda?</h3>
          <ul>
            <li>Asegúrate de usar el email correcto</li>
            <li>Revisa tu carpeta de spam</li>
            <li>El enlace expira en 30 minutos</li>
            <li>Solo puedes tener una solicitud activa por vez</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;